/**
 * The desktop's side of the tunnel: do what the phone asked, and only that.
 *
 * This is the piece with the most to lose. It runs on a machine that can
 * reach a home network, a printer, a router's admin page, and it does what a
 * device on the other side of the internet tells it to. So the rule is
 * narrow and it is enforced here rather than assumed:
 *
 *   - the path must be this app's own api, checked again on arrival even
 *     though the phone checked before sending,
 *   - the address is built here from this window's own origin, never from
 *     anything in the envelope,
 *   - only the headers on the list are passed on.
 *
 * The answer goes back as it arrives, not when it is complete, so a council
 * answer being written token by token reaches the phone token by token.
 */

import API, { API_ORIGIN } from "./api";
import { TUNNEL_PROTOCOL, TunnelError, cut, decodeFrame, encodeFrame } from "./tunnel-protocol";

/** How many recent requests to keep. Enough to see a screen load. */
export const LOG_KEPT = 20;

/**
 * @param channel   something with send(text) and onMessage(handler)
 * @param fetchImpl the real fetch, doing the call against this machine
 * @param origin    where this window's backend lives. Not from the envelope.
 */
export function serveTunnel({
  onBye = null,
  channel,
  fetchImpl = typeof fetch !== "undefined" ? fetch.bind(globalThis) : null,
  origin = API_ORIGIN,
  now = () => Date.now(),
  onLogged = () => {},
}) {
  const running = new Map();
  const log = [];
  // When this side last heard anything at all. A channel can stop carrying
  // without ever firing onclose, and then the desktop sits serving nobody.
  let lastHeard = now();
  // Whether the phone on the other end is one that pings.
  //
  // It decides whether silence means anything. A phone that pings every
  // twenty-five seconds is saying something by going quiet. A phone from an
  // older build never pings at all, and treating its silence as death would
  // hang up on somebody who simply had not tapped anything for a minute.
  // That is not hypothetical: the connect service serves the phone's screen,
  // so it is normal for a Mac to be newer than the phone talking to it.
  let heardPing = false;

  /**
   * What the phone asked and what this machine answered.
   *
   * Kept because the alternative is what happened on 26 July: a phone
   * saying "failed to create new chat" and no way to see, from either side,
   * that every single call was being refused with a 401. Method, path and
   * status only. No bodies, no headers, nothing anyone would mind being on
   * a screen behind them.
   */
  const note = (entry) => {
    log.unshift({ at: now(), ...entry });
    log.length = Math.min(log.length, LOG_KEPT);
    onLogged(log[0]);
  };

  const say = (frame) => {
    try {
      channel.send(encodeFrame(frame));
    } catch {
      // The channel went away mid-answer. There is nobody left to tell.
    }
  };

  const fail = (id, reason) => say({ kind: "fail", id, reason });

  async function carryOut(ask) {
    // Built here, from this window's own origin. Nothing in the envelope
    // decides which machine or which port is contacted.
    const target = `${origin}${ask.path}`;
    const answer = await fetchImpl(target, {
      method: ask.method,
      headers: ask.headers,
      body: ask.body ?? undefined,
    });

    const headers = {};
    answer.headers?.forEach?.((value, name) => {
      headers[String(name).toLowerCase()] = value;
    });
    say({ kind: "head", id: ask.id, status: answer.status, headers });
    note({ method: ask.method, path: ask.path, status: answer.status });

    // Piece by piece when the answer is being written that way, in one go
    // when it is not. Same path either way.
    const reader = answer.body?.getReader?.();
    if (reader) {
      const decoder = new TextDecoder();
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        cut(decoder.decode(value, { stream: true })).forEach((piece) =>
          say({ kind: "chunk", id: ask.id, data: piece })
        );
      }
    } else {
      cut(await answer.text()).forEach((piece) => say({ kind: "chunk", id: ask.id, data: piece }));
    }
    say({ kind: "end", id: ask.id });
  }

  const receive = async (raw) => {
    let ask;
    try {
      ask = decodeFrame(raw);
    } catch (e) {
      // Refused before anything happens. Nothing is contacted, and the phone
      // is told in words rather than left waiting. Only for frames that are
      // ours to begin with: anything else on this channel is not a request
      // that deserves an answer.
      const id = e instanceof TunnelError ? ourId(raw) : null;
      if (id) {
        fail(id, e.message);
        note({ method: "?", path: pathIn(raw), status: "refused" });
      }
      return;
    }
    if (ask.kind === "ping") {
      // The phone asking whether anyone is still there. Answered here rather
      // than in the connection code because this is what already reads every
      // frame, and a ping that had to travel further would be a worse test of
      // whether the channel works.
      lastHeard = now();
      heardPing = true;
      say({ kind: "pong", id: ask.id });
      return;
    }
    if (ask.kind === "bye") {
      // FIX AFSCHEID (15 oktober 2026): de telefoon ververst of sluit. Safari
      // sluit de directe verbinding dan niet altijd netjes; gemeten op Franks
      // iPhone merkte de Mac het vertrek pas na 47 s, via de stilte. Met dit
      // bericht weet hij het meteen.
      onBye?.();
      return;
    }
    if (ask.kind !== "ask") return; // answers are the other side's business
    lastHeard = now();

    running.set(ask.id, true);
    try {
      await carryOut(ask);
    } catch (e) {
      fail(ask.id, e?.message || "This machine could not do that.");
      note({ method: ask.method, path: ask.path, status: "no answer" });
    } finally {
      running.delete(ask.id);
    }
  };

  channel.onMessage?.(receive);

  return {
    receive,
    get busy() {
      return running.size;
    },
    /** Newest first. What the phone asked and what this machine said. */
    get log() {
      return [...log];
    },
    /** When the phone was last heard from, in any shape. */
    get lastHeard() {
      return lastHeard;
    },
    /** Whether silence from this phone means anything. */
    get heardPing() {
      return heardPing;
    },
    /** Where this ends up going, for a screen that wants to say so. */
    servingFrom: API,
  };
}

/** The path out of a refused frame, so the log says which call it was. */
function pathIn(raw) {
  try {
    const parsed = JSON.parse(raw);
    return typeof parsed?.path === "string" ? parsed.path.slice(0, 120) : "unreadable";
  } catch {
    return "unreadable";
  }
}

/**
 * The id out of something of ours that was refused, so the phone can be told
 * which of its calls that was. Null for anything that is not ours: a stray
 * message on this channel is not a question and gets no answer.
 */
function ourId(raw) {
  try {
    const parsed = JSON.parse(raw);
    if (parsed?.v !== TUNNEL_PROTOCOL) return null;
    return typeof parsed.id === "string" ? parsed.id : null;
  } catch {
    return null;
  }
}
